import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  Copy,
  LayoutDashboard,
  Lock,
  Redo2,
  RotateCcw,
  Maximize2,
  Minus,
  Plus,
  Trash2,
  Undo2,
  Unlock,
} from 'lucide-react';
import { useReactFlow } from '@xyflow/react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useEditorStore } from '../state/editor-store';

function ToolButton({
  label,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-sm border border-transparent text-text-secondary transition-colors hover:border-border hover:bg-surface-2 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-30"
      {...props}
    >
      {children}
    </button>
  );
}

export function EditorToolbar() {
  const selectedIds = useEditorStore((state) => state.selectedIds);
  const nodes = useEditorStore((state) => state.graph?.nodes ?? []);
  const pastCount = useEditorStore((state) => state.past.length);
  const futureCount = useEditorStore((state) => state.future.length);
  const clipboardCount = useEditorStore((state) => state.clipboard.length);
  const undo = useEditorStore((state) => state.undo);
  const redo = useEditorStore((state) => state.redo);
  const copy = useEditorStore((state) => state.copy);
  const paste = useEditorStore((state) => state.paste);
  const remove = useEditorStore((state) => state.deleteSelection);
  const setLocked = useEditorStore((state) => state.setLocked);
  const align = useEditorStore((state) => state.align);
  const distribute = useEditorStore((state) => state.distribute);
  const autoLayout = useEditorStore((state) => state.autoLayout);
  const { fitView, setViewport, zoomIn, zoomOut } = useReactFlow();
  const selected = new Set(selectedIds);
  const selectedNodes = nodes.filter((node) => selected.has(node.id));
  const canAlign = selectedNodes.length >= 2 && selectedNodes.some((node) => !node.locked);
  const canDistribute = selectedNodes.length >= 3 && selectedNodes.some((node) => !node.locked);
  const allLocked = selectedNodes.length > 0 && selectedNodes.every((node) => node.locked);

  const fit = () =>
    nodes.length
      ? void fitView({ duration: 220, padding: 0.18 })
      : void setViewport({ x: 0, y: 0, zoom: 1 }, { duration: 220 });

  return (
    <div className="flex flex-wrap items-center gap-1" role="toolbar" aria-label="Ações do editor">
      <ToolButton label="Desfazer (Ctrl+Z)" disabled={!pastCount} onClick={undo}>
        <Undo2 size={16} />
      </ToolButton>
      <ToolButton label="Refazer (Ctrl+Shift+Z)" disabled={!futureCount} onClick={redo}>
        <Redo2 size={16} />
      </ToolButton>
      <span className="mx-1 h-5 w-px bg-[var(--df-border-default)]" />
      <ToolButton label="Copiar seleção (Ctrl+C)" disabled={!selectedIds.length} onClick={copy}>
        <Copy size={16} />
      </ToolButton>
      <ToolButton label="Colar (Ctrl+V)" disabled={!clipboardCount} onClick={paste}>
        <RotateCcw size={16} />
      </ToolButton>
      <ToolButton label="Excluir seleção" disabled={!selectedIds.length} onClick={remove}>
        <Trash2 size={16} />
      </ToolButton>
      <ToolButton
        label={allLocked ? 'Desbloquear seleção' : 'Bloquear seleção'}
        disabled={!selectedIds.length}
        onClick={() => setLocked(!allLocked)}
      >
        {allLocked ? <Unlock size={16} /> : <Lock size={16} />}
      </ToolButton>
      <span className="mx-1 h-5 w-px bg-[var(--df-border-default)]" />
      <ToolButton label="Alinhar à esquerda" disabled={!canAlign} onClick={() => align('left')}>
        <AlignStartVertical size={16} />
      </ToolButton>
      <ToolButton
        label="Centralizar horizontalmente"
        disabled={!canAlign}
        onClick={() => align('center-x')}
      >
        <AlignCenterVertical size={16} />
      </ToolButton>
      <ToolButton label="Alinhar à direita" disabled={!canAlign} onClick={() => align('right')}>
        <AlignEndVertical size={16} />
      </ToolButton>
      <ToolButton label="Alinhar ao topo" disabled={!canAlign} onClick={() => align('top')}>
        <AlignStartHorizontal size={16} />
      </ToolButton>
      <ToolButton
        label="Centralizar verticalmente"
        disabled={!canAlign}
        onClick={() => align('center-y')}
      >
        <AlignCenterHorizontal size={16} />
      </ToolButton>
      <ToolButton label="Alinhar à base" disabled={!canAlign} onClick={() => align('bottom')}>
        <AlignEndHorizontal size={16} />
      </ToolButton>
      <ToolButton
        label="Distribuir horizontalmente"
        disabled={!canDistribute}
        onClick={() => distribute('horizontal')}
      >
        <AlignHorizontalDistributeCenter size={16} />
      </ToolButton>
      <ToolButton
        label="Distribuir verticalmente"
        disabled={!canDistribute}
        onClick={() => distribute('vertical')}
      >
        <AlignVerticalDistributeCenter size={16} />
      </ToolButton>
      <span className="mx-1 h-5 w-px bg-[var(--df-border-default)]" />
      <ToolButton label="Organizar da esquerda para a direita" disabled={!nodes.length} onClick={autoLayout}>
        <LayoutDashboard size={16} />
      </ToolButton>
      <ToolButton label="Diminuir zoom" onClick={() => void zoomOut({ duration: 120 })}>
        <Minus size={16} />
      </ToolButton>
      <ToolButton label="Aumentar zoom" onClick={() => void zoomIn({ duration: 120 })}>
        <Plus size={16} />
      </ToolButton>
      <ToolButton label="Ajustar visualização (F)" onClick={fit}>
        <Maximize2 size={16} />
      </ToolButton>
    </div>
  );
}
